import { useQuery } from '@apollo/client';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useMemo } from 'react';
import { toAnnotationVersionChange } from './AnnotationVersionChangeCard';
import type { BaseNewPostHistoryViewMagneticWallVersionChanges } from './NewPostHistoryView';
import {
  BaseNewPostHistoryView,
  useCalculatedVersionChanges,
} from './NewPostHistoryView';
import VersionChanges from './VersionChanges';
import Page from '../../app/layout/Page';
import Button from '../../components/Button';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillContext from '../../editor/QuillContext';
import { gql } from '../../graphql/generated/gql';
import InternalTopbar from '../../topbar/InternalTopbar';
import postPath from '../../utils/url/postPath';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';

const COMPARE_POSTS_VIEW_QUERY = gql(/** @complexity 15 */ `
  query ComparePostsView($basePostId: ID!, $comparePostId: ID!) {
    basePost: post(id: $basePostId) {
      id
      content(isLatest: true) {
        id
        delta
      }
      annotations {
        id
        ...BaseNewPostHistoryView_annotation
      }
      ...BaseNewPostHistoryView_post
    }
    comparePost: post(id: $comparePostId) {
      id
      title
      updatedAt
      content(isLatest: true) {
        id
        delta
      }
      annotations {
        id
        ...BaseNewPostHistoryView_annotation
      }
    }
    entityAssetToken(entityId: $basePostId, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

function useDiff(baseContent?: Op[], compareContent?: Op[]) {
  return useMemo(() => {
    const diff = new Delta(baseContent)
      .diff(new Delta(compareContent))
      .map((op) => {
        if (op.insert) {
          return {
            ...op,
            attributes: { ...op.attributes, version: 1, diff: 'inserted' },
          };
        }

        if (op.delete) {
          return {
            retain: op.delete,
            attributes: { ...op.attributes, version: 1, diff: 'deleted' },
          };
        }

        return op;
      });
    return new Delta(diff);
  }, [baseContent, compareContent]);
}

interface ComparePostsViewInnerProps {
  basePostId: string;
  comparePostId: string;
}

const ComparePostsViewInner = ({
  basePostId,
  comparePostId,
}: ComparePostsViewInnerProps) => {
  const { data } = useQuery(COMPARE_POSTS_VIEW_QUERY, {
    variables: { basePostId, comparePostId },
  });

  const basePost = data?.basePost;
  const comparePost = data?.comparePost;

  const diff = useDiff(basePost?.content.delta, comparePost?.content.delta);

  const { content, allVersionChanges } = useCalculatedVersionChanges({
    diff: diff.ops,
    snapshot: basePost?.content.delta,
  });

  const magneticWallVersionChanges: BaseNewPostHistoryViewMagneticWallVersionChanges[] =
    useMemo(() => {
      const { allAnnotationIds } = collectThreadAndAnnotationIds(
        content?.ops ?? [],
      );

      return [
        ...(basePost?.annotations
          .map((annotation) => toAnnotationVersionChange(annotation))
          // When the base post and the compare post have the same content,
          // but the base post has an annotation on it, then the diff will
          // nullify the annotation (since it doesn't exist in the compare post).
          // This would lead to annotations without context.
          // Because we don't want to show annotations without context when
          // comparing posts, we filter them out.
          .filter((annotation) => allAnnotationIds.has(annotation.id)) ?? []),
        ...(comparePost?.annotations.map((annotation) =>
          toAnnotationVersionChange(annotation, { isNew: true }),
        ) ?? []),
      ];
    }, [content?.ops, basePost?.annotations, comparePost?.annotations]);

  if (!basePost || !comparePost) return null;

  return (
    <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
      <Page
        topbar={
          <InternalTopbar
            postActions={
              <>
                <Button variant="secondary" to={postPath(basePost)}>
                  Base Post
                </Button>
                <Button variant="secondary" to={postPath(comparePost)}>
                  Compare Post
                </Button>
              </>
            }
          />
        }
      >
        <BaseNewPostHistoryView
          post={basePost}
          editedAt={comparePost.updatedAt}
          allVersionChanges={allVersionChanges}
          content={content}
          magneticWallVersionChanges={magneticWallVersionChanges}
          topbar={
            <VersionChanges.PostLayoutTopbarContainer>
              <VersionChanges.Topbar>
                <VersionChanges.Meta>
                  Comparing to &quot;{comparePost.title}&quot;
                </VersionChanges.Meta>
              </VersionChanges.Topbar>
            </VersionChanges.PostLayoutTopbarContainer>
          }
        />
      </Page>
    </AssetTokenProvider>
  );
};

interface ComparePostsViewProps {
  basePostId: string;
  comparePostId: string;
}

const ComparePostsView = ({
  basePostId,
  comparePostId,
}: ComparePostsViewProps) => {
  return (
    <QuillContext>
      <ComparePostsViewInner
        basePostId={basePostId}
        comparePostId={comparePostId}
      />
    </QuillContext>
  );
};

export default ComparePostsView;
